import axios from 'axios';
import { useAuthStore } from '../store/useAuthStore';
import type { Promotion } from './promotion';
import { getCachedData, setCacheData } from './requestCache';

const API_BASE_URL =
  import.meta.env.VITE_API_URL?.replace(/\/$/, '') ||
  (import.meta.env.DEV ? '/api' : 'https://dehleze.store/api');

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  // Skip Bearer token for auth endpoints (login/register)
  if (config.url?.includes('/auth/') || config.url?.includes('/login') || config.url?.includes('/register')) {
    return config;
  }
  
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshingToken = false;
let refreshWaitQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processRefreshQueue(error: unknown, token: string | null = null) {
  refreshWaitQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else if (token) resolve(token);
  });
  refreshWaitQueue = [];
}

async function refreshAccessToken(): Promise<string> {
  const { refreshToken, updateTokens, logout } = useAuthStore.getState();
  if (!refreshToken) {
    logout();
    throw new Error('Session expired. Please sign in again.');
  }

  const { data } = await axios.post<{ access: string; refresh?: string }>(
    `${API_BASE_URL}/auth/refresh/`,
    { refresh: refreshToken },
    { headers: { 'Content-Type': 'application/json' } }
  );

  const access = data.access;
  if (!access) {
    logout();
    throw new Error('Session expired. Please sign in again.');
  }

  updateTokens(access, data.refresh ?? refreshToken);
  return access;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthRoute =
      originalRequest?.url?.includes('/auth/') ||
      originalRequest?.url?.includes('/login') ||
      originalRequest?.url?.includes('/register');

    const isNetworkFailure =
      !error.response &&
      (error.code === 'ERR_NETWORK' || error.code === 'ENOTFOUND' || error.message?.includes('getaddrinfo') || error.message?.includes('Network Error'));

    if (isNetworkFailure) {
      return Promise.reject(new Error('Network unavailable. Please try again later.'));
    }

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthRoute
    ) {
      if (isRefreshingToken) {
        return new Promise((resolve, reject) => {
          refreshWaitQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshingToken = true;

      try {
        const newToken = await refreshAccessToken();
        processRefreshQueue(null, newToken);
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processRefreshQueue(refreshError, null);
        useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshingToken = false;
      }
    }

    return Promise.reject(error);
  }
);

type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

// Raw API shapes
interface ProductApi {
  id: number;
  name: string;
  base_price: string;
  special_price: string | null;
  main_image: string | null;
  additional_images?: Array<{ image?: string | null; url?: string | null; [key: string]: unknown }> | null;
  images?: Array<{ url?: string | null; image?: string | null; [key: string]: unknown }> | null;
  category_name?: string;
  vendor_id?: number;
  vendor_name?: string;
  store_name?: string;
  vendor_details?: {
    store_name?: string;
    [key: string]: unknown;
  };
  stock?: number;
  feature_product?: boolean;
  status?: string;
  description?: string;
  specification?: Record<string, unknown>;
  // detail responses may have more fields (variants etc.), kept as unknown
  [key: string]: unknown;
}

interface VariantApi {
  id: number;
  product: number;
  sku: string | null;
  color: string;
  size: string;
  price: string;
  stock: number;
  image: string | null;
  custom_attributes: Record<string, unknown>;
}

interface SliderApi {
  id: number;
  title: string;
  image: string;
  status: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface ReviewApi {
  id: number;
  rating?: number | string;
  comment?: string;
  review?: string;
  text?: string;
  user_name?: string;
  user?: unknown;
  created_at?: string;
}

export interface Variant {
  id: number;
  productId: number;
  sku: string | null;
  color: string;
  size: string;
  price: number;
  stock: number;
  image: string | null;
  customAttributes: Record<string, unknown>;
}

export interface Product {
  id: number;
  name: string;
  price: string; // effective price (special or base)
  categoryId?: number;
  base_price?: string;
  special_price?: string | null;
  product_image?: string | null;
  images?: { url: string }[];
  stock?: number;
  promotion?: Promotion;
  variants?: Variant[];
  category_name?: string;
  vendor_name?: string;
  store_name?: string;
  description?: string;
  rating?: number;
  reviewCount?: number;
  specification?: Record<string, unknown>;
  /** Any extra fields from the API not mapped above */
  extra?: Record<string, unknown>;
}

export interface Review {
  id: number;
  rating: number;
  comment: string;
  user_name: string;
  created_at?: string;
}

export interface Category {
  id: number;
  name: string;
  slug?: string;
  image?: string;
  parent_id?: number | null;
  parent?: number | null;
  level?: number;
  children_ids?: number[];
  children?: Category[];
}

const mapVariantApi = (v: VariantApi): Variant => ({
  id: v.id,
  productId: v.product,
  sku: v.sku,
  color: v.color,
  size: v.size,
  price: Number(v.price),
  stock: v.stock,
  image: v.image,
  customAttributes: v.custom_attributes ?? {},
});

const KNOWN_PRODUCT_KEYS = new Set([
  'id', 'name', 'base_price', 'special_price', 'main_image', 'additional_images', 'images', 'category_name',
  'vendor_id', 'vendor_name', 'store_name', 'vendor_details', 'stock', 'feature_product', 'status', 'description', 'variants',
]);

const normalizeImageValue = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const candidate = value as { image?: unknown; url?: unknown; src?: unknown };
    if (typeof candidate.image === 'string') return candidate.image;
    if (typeof candidate.url === 'string') return candidate.url;
    if (typeof candidate.src === 'string') return candidate.src;
  }
  return null;
};

const mapProductImages = (p: ProductApi) => {
  const images: { url: string }[] = [];
  const seen = new Set<string>();
  const pushIfNew = (value: string | null | undefined) => {
    if (!value) return;
    const cleaned = value.trim();
    if (!cleaned || seen.has(cleaned)) return;
    seen.add(cleaned);
    images.push({ url: cleaned });
  };

  pushIfNew(p.main_image);

  if (Array.isArray(p.additional_images)) {
    p.additional_images.forEach((item) => pushIfNew(normalizeImageValue(item)));
  }

  if (Array.isArray(p.images)) {
    p.images.forEach((item) => pushIfNew(normalizeImageValue(item)));
  }

  return {
    product_image: images[0]?.url ?? p.main_image ?? null,
    images: images.length ? images : undefined,
  };
};

function extractResultsFromApiResponse<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (!data || typeof data !== 'object') return [];

  const record = data as Record<string, unknown>;
  if (Array.isArray(record.results)) return record.results as T[];
  if (Array.isArray(record.data)) return record.data as T[];
  if (Array.isArray((record as { items?: unknown }).items)) return (record as { items: T[] }).items;
  return [];
}

const mapProductApi = (p: ProductApi): Product => {
  const effectivePrice = p.special_price ?? p.base_price ?? '0';
  const extra: Record<string, unknown> = {};
  Object.keys(p).forEach((k) => {
    if (!KNOWN_PRODUCT_KEYS.has(k)) extra[k] = (p as Record<string, unknown>)[k];
  });
  const { product_image, images } = mapProductImages(p);
  // Extract store_name from vendor_details if available
  const storeName = p.vendor_details?.store_name || p.store_name || p.vendor_name;
  return {
    id: p.id,
    name: p.name,
    price: effectivePrice,
    base_price: p.base_price,
    special_price: p.special_price,
    product_image,
    images,
    stock: p.stock,
    category_name: p.category_name,
    vendor_name: storeName,
    store_name: storeName,
    description: typeof p.description === 'string' ? p.description : undefined,
    specification: typeof p.specification === 'object' && p.specification !== null ? (p.specification as Record<string, unknown>) : undefined,
    variants: Array.isArray((p as ProductApi & { variants?: VariantApi[] }).variants)
      ? (p as ProductApi & { variants: VariantApi[] }).variants.map(mapVariantApi)
      : undefined,
    ...(Object.keys(extra).length ? { extra } : {}),
      categoryId: (
        (p as unknown as Record<string, unknown>).category as number | undefined) ??
        (typeof (p as unknown as Record<string, unknown>).category_id === 'number'
          ? (p as unknown as Record<string, unknown>).category_id as number
          : typeof ((p as unknown as any).category)?.id === 'number'
          ? ((p as unknown as any).category as any).id
          : undefined),
  };
};

const mapReviewApi = (r: ReviewApi): Review => {
  const rawRating =
    r.rating ??
    ((r as unknown as Record<string, unknown>).stars ??
      (r as unknown as Record<string, unknown>).score);
  const ratingNumber = rawRating != null ? Number(rawRating) : 0;
  const comment =
    r.comment ??
    r.review ??
    r.text ??
    (typeof (r as unknown as Record<string, unknown>).detail === 'string'
      ? (r as unknown as Record<string, unknown>).detail
      : '');

  let userName = r.user_name ?? '';
  const u = r.user as unknown;
  if (!userName && u) {
    if (typeof u === 'string') userName = u;
    else if (typeof u === 'object' && u !== null) {
      const obj = u as { username?: string; name?: string };
      userName = obj.username || obj.name || '';
    }
  }

  return {
    id: r.id,
    rating: Number.isNaN(ratingNumber) ? 0 : ratingNumber,
    comment: String(comment ?? ''),
    user_name: userName || 'Anonymous',
    created_at: r.created_at,
  };
};

// Products

export async function fetchProducts(
  filters?: Record<string, unknown>
): Promise<Product[]> {
  const params: Record<string, unknown> = {
    page_size: 24,
    format: 'json',
    ...filters,
  };
  const { data } = await api.get<PaginatedResponse<ProductApi>>('/products/', {
    params,
  });
  return (data.results ?? []).map(mapProductApi);
}

export interface ProductsWithCount {
  results: Product[];
  count: number;
}

export async function fetchProductsWithCount(
  filters?: Record<string, unknown>,
  pageSize = 12
): Promise<ProductsWithCount> {
  const params: Record<string, unknown> = {
    page: 1,
    page_size: pageSize,
    format: 'json',
    ...filters,
  };

  try {
    const { data } = await api.get<PaginatedResponse<ProductApi> | ProductApi[] | Record<string, unknown>>('/products/', {
      params,
    });
    const results = extractResultsFromApiResponse<ProductApi>(data);
    return {
      results: results.map(mapProductApi),
      count: typeof (data as { count?: number }).count === 'number' ? (data as { count: number }).count : results.length,
    };
  } catch {
    return { results: [], count: 0 };
  }
}

export async function fetchAllProducts(
  filters?: Record<string, unknown>,
): Promise<ProductsWithCount> {
  let allResults: Product[] = [];
  let page = 1;
  let hasMore = true;
  const pageSize = 50; // Fetch 50 items per page
  
  while (hasMore) {
    const params: Record<string, unknown> = {
      page,
      page_size: pageSize,
      format: 'json',
      ...filters,
    };
    const { data } = await api.get<PaginatedResponse<ProductApi>>('/products/', {
      params,
    });
    
    allResults = [...allResults, ...(data.results ?? []).map(mapProductApi)];
    hasMore = data.next !== null && data.next !== undefined;
    page++;
  }
  
  return {
    results: allResults,
    count: allResults.length,
  };
}

/** Collect category id and all descendant category ids from a flat list. */
export function getDescendantCategoryIds(
  categories: Category[],
  parentId: number
): number[] {
  const ids = new Set<number>([parentId]);
  let added = true;
  while (added) {
    added = false;
    for (const c of categories) {
      const pid = c.parent_id ?? c.parent;
      if (pid != null && ids.has(pid) && !ids.has(c.id)) {
        ids.add(c.id);
        added = true;
      }
    }
  }
  return Array.from(ids);
}

export async function fetchProductsByCategory(
  categoryId: number,
  includeSubcategories: boolean,
  limit = 48
): Promise<Product[]> {
  const params: Record<string, unknown> = {
    format: 'json',
    page_size: limit,
  };
  if (includeSubcategories) {
    params.include_sub = true;
  }

  try {
    const { data } = await api.get<PaginatedResponse<ProductApi> | ProductApi[] | Record<string, unknown>>(
      `/categories/${categoryId}/products/`,
      { params }
    );
    const results = extractResultsFromApiResponse<ProductApi>(data);
    if (results.length > 0) {
      return results.map(mapProductApi).slice(0, limit);
    }
  } catch {
    // fall back to the main products endpoint below
  }

  try {
    const { results } = await fetchProductsWithCount({}, Math.max(limit, 200));
    const categoryIds = includeSubcategories
      ? getDescendantCategoryIds(await fetchAllCategoriesFlat(), categoryId)
      : [categoryId];

    const filtered = results.filter((product) => {
      const productCategoryId = product.categoryId;
      return typeof productCategoryId === 'number' && categoryIds.includes(productCategoryId);
    });

    return filtered.slice(0, limit);
  } catch {
    return [];
  }
}

export async function fetchProductsByUser(
  userId: number,
  limit = 100
): Promise<Product[]> {
  const cacheKey = `productsByUser:${userId}`;
  const cached = getCachedData<Product[]>(cacheKey);
  if (cached) return cached;

  try {
    const { data } = await api.get<PaginatedResponse<ProductApi>>(`/products/by-user/${userId}/`, {
      params: { format: 'json', page_size: limit },
    });
    const products = (data.results ?? []).map(mapProductApi);
    setCacheData(cacheKey, products, 5 * 60 * 1000); // Cache for 5 minutes
    return products;
  } catch (error) {
    console.error(`Failed to fetch products for user ${userId}:`, error);
    return [];
  }
}

/** Returns only products where feature_product is set (truthy) in the API, scanning a few pages. */
export async function fetchFeaturedProducts(limit = 12): Promise<Product[]> {
  // v2 cache key so we ignore any older empty cached data
  const cacheKey = `featuredProducts:v2:${limit}`;
  const cached = getCachedData<Product[]>(cacheKey);
  if (cached && cached.length > 0) return cached;

  const isFeatured = (p: ProductApi): boolean => {
    const raw = (p as ProductApi & { feature_product?: unknown }).feature_product;
    if (raw === true) return true;
    if (typeof raw === 'number') return raw === 1;
    if (typeof raw === 'string') {
      const lower = String(raw).toLowerCase();
      return lower === '1' || lower === 'true';
    }
    return false;
  };

  const featured: ProductApi[] = [];

  try {
    const pageSize = 50;
    const maxPages = 4; // safety limit to avoid too many requests

    // First page
    let pagesFetched = 0;
    let nextUrl: string | null = null;
    const first = await api.get<PaginatedResponse<ProductApi>>('/products/', {
      params: { format: 'json', page_size: pageSize },
    });
    pagesFetched += 1;
    featured.push(...(first.data.results ?? []).filter(isFeatured));
    nextUrl = first.data.next ?? null;

    // Walk through a few remaining pages until we have enough or hit limits
    while (nextUrl && featured.length < limit && pagesFetched < maxPages) {
      const res = await api.get<PaginatedResponse<ProductApi>>(nextUrl);
      pagesFetched += 1;
      featured.push(...(res.data.results ?? []).filter(isFeatured));
      nextUrl = res.data.next ?? null;
    }
  } catch {
    // ignore errors, we'll just return whatever we collected (possibly empty)
  }

  const mapped = featured.slice(0, limit).map(mapProductApi);
  if (mapped.length > 0) {
    // Cache for 1 hour to avoid hitting the server too often
    setCacheData(cacheKey, mapped, 60 * 60 * 1000);
    return mapped;
  }

  // Fallback: if no featured flag is set in the API,
  // show the first few products so the section is never empty.
  const { data } = await api.get<PaginatedResponse<ProductApi>>('/products/', {
    params: { format: 'json', page_size: limit },
  });
  const fallback = (data.results ?? []).map(mapProductApi);
  setCacheData(cacheKey, fallback, 60 * 60 * 1000);
  return fallback;
}

export async function fetchOnSaleProducts(limit = 12): Promise<Product[]> {
  const cacheKey = `onSaleProducts:${limit}`;
  const cached = getCachedData<Product[]>(cacheKey);
  if (cached) return cached;

  const params = {
    page_size: limit,
    format: 'json',
    special_price__isnull: false,
  };
  try {
    const { data } = await api.get<PaginatedResponse<ProductApi>>('/products/', {
      params,
    });
    const onSale = data.results
      .filter((p) => p.special_price != null && p.special_price !== '')
      .slice(0, limit)
      .map(mapProductApi);
    setCacheData(cacheKey, onSale);
    return onSale;
  } catch {
    const { data } = await api.get<PaginatedResponse<ProductApi>>('/products/', {
      params: { page_size: limit * 3, format: 'json' },
    });
    const onSale = data.results
      .filter((p) => p.special_price != null && p.special_price !== '')
      .slice(0, limit)
      .map(mapProductApi);
    setCacheData(cacheKey, onSale);
    return onSale;
  }
}

export interface ProductsPageResult {
  results: Product[];
  next: string | null;
  count: number;
}

export async function fetchProductsPage(
  page = 1,
  pageSize = 16
): Promise<ProductsPageResult> {
  const { data } = await api.get<PaginatedResponse<ProductApi>>('/products/', {
    params: { format: 'json', page, page_size: pageSize },
  });
  return {
    results: data.results.map(mapProductApi),
    next: data.next,
    count: data.count,
  };
}

export async function fetchProductBySlug(slug: string): Promise<Product> {
  const { data } = await api.get<ProductApi>(`/products/slug/${slug}/`, {
    params: { format: 'json' },
  });
  return mapProductApi(data);
}

export async function fetchProductById(id: number): Promise<Product> {
  const cacheKey = `product:${id}`;
  const cached = getCachedData<Product>(cacheKey);
  if (cached) return cached;

  const { data } = await api.get<ProductApi>(`/products/${id}/`, {
    params: { format: 'json' },
  });
  const mapped = mapProductApi(data);
  setCacheData(cacheKey, mapped);
  return mapped;
}

export async function fetchProductVariants(productId: number): Promise<Variant[]> {
  const cacheKey = `variants:${productId}`;
  const cached = getCachedData<Variant[]>(cacheKey);
  if (cached) return cached;

  const { data } = await api.get<PaginatedResponse<VariantApi>>('/variants/', {
    params: { product: productId, format: 'json' },
  });
  const variants = data.results
    // Safety: filter by product id in case backend ignores filter
    .filter((v) => v.product === productId)
    .map(mapVariantApi);
  setCacheData(cacheKey, variants);
  return variants;
}

// Categories

export async function fetchCategoryTree(): Promise<Category[]> {
  const cacheKey = 'categories:tree';
  const cached = getCachedData<Category[]>(cacheKey);
  if (cached) return cached;

  const { data } = await api.get<Category[]>('/categories/tree/', {
    params: { format: 'json' },
  });
  // categories rarely change; cache for 24h
  setCacheData(cacheKey, data, 24 * 60 * 60 * 1000);
  return data;
}

export async function fetchRootCategories(limit = 12): Promise<Category[]> {
  const cacheKey = `categories:root:${limit}`;
  const cached = getCachedData<Category[]>(cacheKey);
  if (cached) return cached;

  const { data } = await api.get<PaginatedResponse<Category>>('/categories/', {
    params: { format: 'json', parent__isnull: true, page_size: limit },
  });
  setCacheData(cacheKey, data.results, 24 * 60 * 60 * 1000);
  return data.results;
}

/** Fetches parent categories from https://dehleze.store/api//categories/parents/ */
export async function fetchParentCategories(): Promise<Category[]> {
  const cacheKey = 'categories:parents';
  const cached = getCachedData<Category[]>(cacheKey);
  if (cached) return cached;

  const { data } = await api.get<PaginatedResponse<Category>>('/categories/parents/', {
    params: { format: 'json' },
  });
  const results = normalizeCategories(data.results ?? []);
  // parent categories used on home; cache for 24h
  setCacheData(cacheKey, results, 24 * 60 * 60 * 1000);
  return results;
}

function normalizeCategories(items: unknown): Category[] {
  if (!Array.isArray(items)) return [];
  return items.map((c: Record<string, unknown>) => {
    const parentVal = c.parent_id ?? c.parent;
    const parentId = parentVal == null ? null : typeof parentVal === 'number' ? parentVal : Number(parentVal);
    const childrenIds = Array.isArray(c.children_ids) ? (c.children_ids as number[]) : [];
    return {
      id: Number(c.id),
      name: String(c.name ?? ''),
      slug: c.slug != null ? String(c.slug) : undefined,
      image: c.image !== undefined && c.image !== null ? String(c.image) : undefined,
      parent_id: parentId,
      parent: parentId,
      level: typeof c.level === 'number' ? c.level : undefined,
      children_ids: childrenIds.length ? childrenIds : undefined,
    };
  });
}

export async function fetchAllParentCategories(): Promise<Category[]> {
  const cacheKey = 'categories:allParents';
  const cached = getCachedData<Category[]>(cacheKey);
  if (cached) return cached;

  const all: Category[] = [];
  try {
    const first = await api.get<PaginatedResponse<Category> | Category[]>('/categories/', {
      params: { format: 'json', parent__isnull: true, page_size: 200 },
    });
    const raw = first.data as PaginatedResponse<Category> | Category[];
    const results = Array.isArray(raw) ? raw : (raw?.results ?? []);
    let nextUrl: string | null = Array.isArray(raw) ? null : (raw?.next ?? null);
    all.push(...normalizeCategories(results));
    while (nextUrl) {
      const res = await api.get<PaginatedResponse<Category>>(nextUrl);
      const nextResults = res.data?.results ?? [];
      all.push(...normalizeCategories(nextResults));
      nextUrl = res.data?.next ?? null;
    }
  } catch {
    // fallback: try tree or flat
  }
  if (all.length <= 1) {
    try {
      const flatRes = await api.get<PaginatedResponse<Category> | Category[]>('/categories/', {
        params: { format: 'json', page_size: 500 },
      });
      const raw = flatRes.data as PaginatedResponse<Category> | Category[];
      const flat = Array.isArray(raw) ? raw : (raw?.results ?? []);
      const normalized = normalizeCategories(flat);
      const parents = normalized.filter((c) => c.parent_id == null && c.parent == null);
      if (parents.length > 0) {
        setCacheData(cacheKey, parents, 24 * 60 * 60 * 1000);
        return parents;
      }
    } catch {
      try {
        const tree = await api.get<Category[]>('/categories/tree/', { params: { format: 'json' } });
        const list = Array.isArray(tree.data) ? tree.data : [];
        const roots = normalizeCategories(list).filter((c) => c.parent_id == null && c.parent == null);
        if (roots.length > 0) {
          setCacheData(cacheKey, roots, 24 * 60 * 60 * 1000);
          return roots;
        }
      } catch {
        // leave all as is
      }
    }
  }
  setCacheData(cacheKey, all, 24 * 60 * 60 * 1000);
  return all;
}

export async function fetchAllCategoriesFlat(): Promise<Category[]> {
  const cacheKey = 'categories:flat';
  const cached = getCachedData<Category[]>(cacheKey);
  if (cached) return cached;

  const all: Category[] = [];
  let nextUrl: string | null = null;
  const first = await api.get<PaginatedResponse<Category> | Category[]>('/categories/', {
    params: { format: 'json', page_size: 200 },
  });
  const raw = first.data as PaginatedResponse<Category> | Category[];
  const results = Array.isArray(raw) ? raw : (raw?.results ?? []);
  all.push(...normalizeCategories(results));
  nextUrl = Array.isArray(raw) ? null : (raw?.next ?? null);
  while (nextUrl) {
    const res = await api.get<PaginatedResponse<Category>>(nextUrl);
    all.push(...normalizeCategories(res.data?.results ?? []));
    nextUrl = res.data?.next ?? null;
  }
  setCacheData(cacheKey, all, 24 * 60 * 60 * 1000);
  return all;
}

export async function fetchCategoryById(id: number): Promise<Category | null> {
  try {
    const { data } = await api.get<Record<string, unknown>>(`/categories/${id}/`, {
      params: { format: 'json' },
    });
    return normalizeCategories([data])[0] ?? null;
  } catch {
    return null;
  }
}

export interface Slider {
  id: number;
  title: string;
  image: string;
  status: boolean;
  display_order: number;
}

export async function fetchSliders(): Promise<Slider[]> {
  const cacheKey = 'sliders:all';
  const cached = getCachedData<Slider[]>(cacheKey);
  if (cached) return cached;

  const { data } = await api.get<PaginatedResponse<SliderApi>>('/sliders/', {
    params: { format: 'json' },
  });
  const mapped = data.results
    .filter((s) => s.status)
    .sort((a, b) => a.display_order - b.display_order)
    .map((s) => ({
      id: s.id,
      title: s.title,
      image: s.image,
      status: s.status,
      display_order: s.display_order,
    }));
  // sliders change rarely; cache for 24h
  setCacheData(cacheKey, mapped, 24 * 60 * 60 * 1000);
  return mapped;
}

export async function fetchProductReviews(productId: number): Promise<Review[]> {
  const cacheKey = `reviews:product:${productId}`;
  const cached = getCachedData<Review[]>(cacheKey);
  if (cached) return cached;

  try {
    const { data } = await api.get<PaginatedResponse<ReviewApi>>('/reviews/', {
      params: { format: 'json', product: productId, page_size: 50 },
    });
    const list = (data.results ?? []).map(mapReviewApi);
    setCacheData(cacheKey, list, 5 * 60 * 1000);
    return list;
  } catch {
    return [];
  }
}

export function flatToTree(categories: Category[]): Category[] {
  const map = new Map<number, Category & { children: Category[] }>();
  categories.forEach((c) => map.set(c.id, { ...c, children: [] }));
  const roots: Category[] = [];
  categories.forEach((c) => {
    const node = map.get(c.id)!;
    if (!c.parent_id) {
      roots.push(node);
    } else {
      const parent = map.get(c.parent_id);
      if (parent) (parent as Category).children!.push(node);
      else roots.push(node);
    }
  });
  return roots;
}

// Orders

export interface OrderItemPayload {
  product: number;
  quantity: number;
  price: number;
  specification: Record<string, unknown>;
  variant_id?: number;
}

export interface CreateOrderPayload {
  guest_name: string;
  guest_email: string;
  guest_phone: string;
  guest_address: string;
  subtotal: number;
  delivery_charges: number;
  payment_status: string;
  status: string;
  order_items: OrderItemPayload[];
  order_notes: string;
  use_frontend_prices: boolean;
}

export async function createOrder(payload: CreateOrderPayload): Promise<{ data: unknown }> {
  console.log('🚀 Creating order with payload:', JSON.stringify(payload, null, 2));
  try {
    const { data } = await api.post('/orders/', payload);
    console.log('✅ Order created successfully:', data);
    return { data };
  } catch (error) {
    console.error('❌ Order creation failed:', error);
    throw error;
  }
}

// Authentication

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  role?: string;
  profile_pic?: string | null;
  [key: string]: unknown;
}

export interface AuthResponse {
  user: AuthUser;
  token: string;
  refresh: string;
}

export interface LoginPayload {
  identifier?: string;
  email?: string;
  password: string;
}

export interface RegisterPayload {
  firstName?: string;
  lastName?: string;
  username: string;
  email: string;
  password: string;
  phone?: string;
  address?: string;
}

function mapAuthUser(user: Record<string, unknown>, fallbackEmail = ''): AuthUser {
  return {
    id: user.id as number,
    username: (user.username as string) || (user.email as string) || `user-${user.id}`,
    email: (user.email as string) || fallbackEmail,
    name: (user.name as string) || '',
    role: (user.role as string) || 'customer',
    profile_pic: (user.profile_pic as string | null) ?? (user.profile_picture as string | null) ?? null,
    phone: (user.phone as string) || '',
    address: (user.address as string) || '',
    ...user,
  };
}

function extractApiErrorMessage(error: any, fallback: string): string {
  const data = error.response?.data;
  if (!data) return fallback;

  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.error === 'string') return data.error;
  if (typeof data.message === 'string') return data.message;

  if (typeof data === 'object' && data !== null) {
    for (const value of Object.values(data)) {
      if (Array.isArray(value) && typeof value[0] === 'string') {
        return value[0];
      }
    }
  }

  return fallback;
}

export async function loginUser(credentials: LoginPayload): Promise<AuthResponse> {
  const email = (credentials.email ?? credentials.identifier ?? '').trim();
  if (!email) {
    throw new Error('Email is required.');
  }

  const payload = {
    email,
    password: credentials.password,
  };

  try {
    const { data } = await api.post<any>('/auth/login/', payload);
    const user = data.user || data.data?.user || {};
    const token = data.access || data.token || data.access_token || data.data?.token;
    const refresh = data.refresh || data.refresh_token || data.data?.refresh;

    if (!user?.id || !token) {
      throw new Error('Invalid login response from server.');
    }

    return {
      user: mapAuthUser(user, email),
      token,
      refresh: refresh || token,
    };
  } catch (error: any) {
    throw new Error(extractApiErrorMessage(error, 'Login failed. Please check your credentials.'));
  }
}

export async function registerUser(data: RegisterPayload): Promise<AuthResponse> {
  const name = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
  const payload = {
    username: data.username.trim(),
    email: data.email.trim(),
    password: data.password,
    name,
    phone: data.phone?.trim() || '',
    address: data.address?.trim() || '',
  };

  try {
    const { data: response } = await api.post<any>('/auth/register/', payload);
    const user = response.user || response.data?.user || {};
    const token = response.access || response.token || response.access_token || response.data?.token;
    const refresh = response.refresh || response.refresh_token || response.data?.refresh;

    if (!user?.id || !token) {
      throw new Error('Invalid registration response from server.');
    }

    return {
      user: mapAuthUser(user, data.email),
      token,
      refresh: refresh || token,
    };
  } catch (error: any) {
    throw new Error(extractApiErrorMessage(error, 'Registration failed. Please try again.'));
  }
}

export interface TrackOrderPayload {
  orderId?: string;
  trackingCode?: string;
  email?: string;
}

export async function trackOrder(payload: TrackOrderPayload): Promise<any> {
  try {
    if (!payload.email?.trim()) {
      throw new Error('Please provide an email address to track your orders.');
    }

    const email = payload.email.trim().toLowerCase();
    console.log(`🔍 Tracking orders for email:`, email);

    let orders: any[] = [];
    let foundOrders = false;

    // Try email-based query endpoints first
    const emailEndpoints = [
      `/orders?email=${encodeURIComponent(payload.email.trim())}`,
      `/orders/email/${encodeURIComponent(payload.email.trim())}`,
      `/orders/search?email=${encodeURIComponent(payload.email.trim())}`,
      `/api/orders?email=${encodeURIComponent(payload.email.trim())}`,
    ];

    for (const endpoint of emailEndpoints) {
      try {
        console.log(`📡 Trying email endpoint: ${endpoint}`);
        const { data } = await api.get(endpoint);

        if (Array.isArray(data)) {
          orders = data;
          foundOrders = true;
          console.log(`✅ Found orders at email endpoint:`, orders.length);
          break;
        }

        if (data?.results && Array.isArray(data.results)) {
          orders = data.results;
          foundOrders = true;
          console.log(`✅ Found orders at email endpoint:`, orders.length);
          break;
        }

        if (data?.orders && Array.isArray(data.orders)) {
          orders = data.orders;
          foundOrders = true;
          console.log(`✅ Found orders at email endpoint:`, orders.length);
          break;
        }

        if (data?.order && !Array.isArray(data.order)) {
          orders = [data.order];
          foundOrders = true;
          console.log(`✅ Found single order at email endpoint`);
          break;
        }

        if (data?.data && Array.isArray(data.data)) {
          orders = data.data;
          foundOrders = true;
          console.log(`✅ Found orders in data wrapper:`, orders.length);
          break;
        }
      } catch (err) {
        console.warn(`⚠️ Email endpoint failed, trying next...`);
        continue;
      }
    }

    // If email endpoints failed, try fetching all orders and filter
    if (!foundOrders) {
      console.log(`📡 Email endpoints failed, trying to fetch all orders...`);
      const allOrdersEndpoints = [
        '/orders/',
        '/orders/list/',
        '/api/orders/',
        '/orders/all/',
        '/orders',
      ];

      for (const endpoint of allOrdersEndpoints) {
        try {
          console.log(`📡 Trying general endpoint: ${endpoint}`);
          const { data } = await api.get(endpoint);

          if (Array.isArray(data)) {
            orders = data;
            foundOrders = true;
            console.log(`✅ Found all orders at ${endpoint}:`, orders.length);
            break;
          }

          if (data?.results && Array.isArray(data.results)) {
            orders = data.results;
            foundOrders = true;
            console.log(`✅ Found all orders at ${endpoint}:`, orders.length);
            break;
          }

          if (data?.orders && Array.isArray(data.orders)) {
            orders = data.orders;
            foundOrders = true;
            console.log(`✅ Found all orders at ${endpoint}:`, orders.length);
            break;
          }

          if (data?.data && Array.isArray(data.data)) {
            orders = data.data;
            foundOrders = true;
            console.log(`✅ Found all orders in data wrapper:`, orders.length);
            break;
          }
        } catch (err) {
          console.warn(`⚠️ General endpoint ${endpoint} failed, trying next...`);
          continue;
        }
      }
    }

    if (!foundOrders || orders.length === 0) {
      console.error('❌ No endpoints returned valid order data');
      throw new Error('Unable to fetch orders. Please check your email and try again later.');
    }

    // Match orders by email
    const matchedOrders = orders.filter((order) => {
      const orderEmail =
        order?.customer_details?.user_details?.email?.toLowerCase() ||
        order?.customer_details?.email?.toLowerCase() ||
        order?.guest_email?.toLowerCase() ||
        order?.email?.toLowerCase() ||
        order?.user_email?.toLowerCase() ||
        order?.buyer_email?.toLowerCase() ||
        '';

      return orderEmail === email;
    });

    if (matchedOrders.length === 0) {
      console.warn(`⚠️ No orders found matching email: ${email}`);
      throw new Error(`No orders found for email: ${payload.email}`);
    }

    console.log(`✅ Found ${matchedOrders.length} order(s) for email: ${email}`, matchedOrders);

    return {
      orders: matchedOrders,
      order: matchedOrders[0] ?? null,
    };
  } catch (error: any) {
    const errorMsg = 
      error.response?.data?.detail ||
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Unable to track order. Please try again.';

    console.error('❌ Order tracking failed:', errorMsg, error);
    throw new Error(errorMsg);
  }
}

